import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(n: number | undefined | null, opts?: { compact?: boolean }) {
  if (n == null || Number.isNaN(n)) return '—'
  if (opts?.compact) {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`
    if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`
    return `$${n.toFixed(0)}`
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

export function formatRelativeDate(iso: string | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const ms = Date.now() - d.getTime()
  const s = Math.floor(ms / 1000)
  if (s < 60) return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const days = Math.floor(h / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

export function formatDateLong(iso: string | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

export function entityColor(entity: string): { bg: string; text: string; border: string } {
  const palette: Record<string, { bg: string; text: string; border: string }> = {
    ARH:      { bg: 'bg-gold/15',     text: 'text-gold-soft',  border: 'border-gold/40'   },
    RRM:      { bg: 'bg-bronze/15',   text: 'text-bronze',     border: 'border-bronze/50' },
    TSA:      { bg: 'bg-info/15',     text: 'text-info',       border: 'border-info/40'   },
    FTM:      { bg: 'bg-success/15', text: 'text-success',    border: 'border-success/40'},
    DRIJEZIE: { bg: 'bg-warning/15',  text: 'text-warning',    border: 'border-warning/40'},
    personal: { bg: 'bg-cream/10',    text: 'text-cream',      border: 'border-cream/30'  },
    unknown:  { bg: 'bg-ink-muted/10',text: 'text-ink-muted',  border: 'border-ink-muted/30'},
  }
  return palette[entity] ?? palette.unknown
}

export function statusColor(status: string): { bg: string; text: string; dot: string } {
  switch (status) {
    case 'active':         return { bg: 'bg-success/10', text: 'text-success', dot: 'bg-success' }
    case 'wip':            return { bg: 'bg-warning/10', text: 'text-warning', dot: 'bg-warning' }
    case 'dormant':        return { bg: 'bg-ink-muted/10', text: 'text-ink-muted', dot: 'bg-ink-muted' }
    case 'decommissioned': return { bg: 'bg-danger/10', text: 'text-danger', dot: 'bg-danger' }
    case 'archived':       return { bg: 'bg-bronze/10', text: 'text-bronze', dot: 'bg-bronze' }
    default:               return { bg: 'bg-cream/10', text: 'text-cream', dot: 'bg-cream' }
  }
}

export const ENTITIES: readonly string[] = ['ARH', 'RRM', 'TSA', 'FTM', 'DRIJEZIE', 'personal', 'unknown']
export const STATUSES: readonly string[] = ['active', 'wip', 'dormant', 'decommissioned', 'archived']
export const CATEGORIES: readonly string[] = [
  'edtech', 'creative-media-saas', 'healthcare-edtech', 'internal-tooling',
  'marketing-site', 'photography-tool', 'presentation-tool', 'ops-tool', 'archive', 'other',
]
